// Multi-series alignment. Each series carries its own `points` array
// (variable length, may have different endDate coverage). The chart
// needs a single category axis sourced from the UNION of every
// series's endDates, with each series's data array padded to that
// shared category index (null for missing periods -- ECharts renders
// gaps for line series with `connectNulls={false}`, blank slots for
// bar series).
//
// Used by both TrendLineChart and TrendBarChart so they share period
// alignment behavior even though their visual representations differ.

import { formatPeriodLabel } from './trendTooltip'

/**
 * @param {Array<{ id, name, points, valueFormat? }>} seriesList
 * @param {string} frequency -- resolved frequency, used for period labels
 * @returns {{
 *   categories: Array<string>,  -- ordered period labels
 *   endDates: Array<string>,    -- parallel raw endDates (debug + tooltip fallback)
 *   alignedSeries: Array<{ id, name, valueFormat, data }>  -- one per input series, data[i] padded to categories[i]
 * }}
 */
export function alignSeriesToSharedAxis (seriesList, frequency) {
  const list = Array.isArray(seriesList) ? seriesList : []
  // Union of endDates across all series, preserving chronological order.
  // Use string-sortable ISO dates so a lexicographic sort is also a
  // chronological sort.
  const set = new Set()
  for (const s of list) {
    for (const p of s?.points || []) {
      if (p?.endDate) set.add(p.endDate)
    }
  }
  const endDates = Array.from(set).sort()
  const categories = endDates.map(d => formatPeriodLabel(d, frequency))

  const alignedSeries = list.map(s => {
    const byDate = new Map()
    for (const p of s?.points || []) {
      if (p?.endDate) byDate.set(p.endDate, p)
    }
    const data = endDates.map((d, i) => {
      const p = byDate.get(d)
      if (!p) {
        // Padding slot. Keep `value: null` so ECharts treats it as a
        // gap (line) or empty slot (bar); still carry periodLabel so
        // tooltip lookups don't fall back to a bare numeric index.
        return { value: null, endDate: d, periodLabel: categories[i] }
      }
      return {
        value: p.value,
        endDate: p.endDate,
        periodLabel: categories[i]
      }
    })
    return {
      id: s.id,
      name: s.name,
      valueFormat: s.valueFormat,
      data
    }
  })

  return { categories, endDates, alignedSeries }
}
